import { VendorBooking } from '../types/Vendor';
import { z, VendorBookingSchema } from '@guestguru/shared';
import { apiGet, planningPath } from './PlanningApiClient';

export const VENDOR_BOOKINGS_UPDATED_EVENT = 'anojeje:vendor-bookings-updated';

// ── API response shape (snake_case from @guestguru/shared) ──

type ApiVendorBooking = z.infer<typeof VendorBookingSchema>;

// A handful of pre-canonicalization rows may still carry these legacy
// values — normalize on read so old data displays with the same
// vocabulary as anything written going forward.
const LEGACY_STATUS_ALIASES: Record<string, VendorBooking['status']> = {
  prospecting: 'inquiry',
  shortlisted: 'inquiry',
  confirmed: 'booked',
};

// The full canonical set, kept beside the type it mirrors. Used to VALIDATE,
// not just to cast — see normalizeBookingStatus below.
const KNOWN_BOOKING_STATUSES: ReadonlySet<string> = new Set<VendorBooking['status']>([
  'inquiry', 'quoted', 'negotiating', 'booked',
  'contract-signed', 'paid-in-full', 'rejected', 'cancelled',
]);

function normalizeBookingStatus(status: string | null | undefined): VendorBooking['status'] {
  if (!status) return 'inquiry';
  const aliased = LEGACY_STATUS_ALIASES[status];
  if (aliased) return aliased;

  // This used to be `status as VendorBooking['status']` — an unchecked cast.
  // Any value in the column outside the eight canonical statuses (and the
  // three legacy aliases) was waved through as if it were valid, which then
  // made TypeScript believe every downstream lookup keyed by it was total.
  // It is not: VENDOR_BOOKING_STATUS_LABELS[status] returned undefined, and
  // React renders undefined as nothing — so the vendor's status chip drew as
  // an empty, uncoloured badge. That is the "vendor booking with no status"
  // report. Fall back to the same 'inquiry' default an absent status gets.
  if (!KNOWN_BOOKING_STATUSES.has(status)) {
    console.warn(`[VendorBookingService] unrecognised booking status "${status}" — falling back to "inquiry"`);
    return 'inquiry';
  }
  return status as VendorBooking['status'];
}

// ── Transform API → local shape ──

function transformApiBooking(row: ApiVendorBooking): VendorBooking {
  return {
    id: row.id == null ? undefined : String(row.id),
    vendorId: row.vendor_id,
    status: normalizeBookingStatus(row.status),
    eventId: row.event_id || undefined,
    // A booking can span several events — VB-016 covers 2, 4 and 5. event_id
    // is the legacy scalar and only ever held the first of them.
    eventIds: Array.isArray(row.event_ids) ? row.event_ids.map(String) : undefined,
    bookingDate: row.booking_date || undefined,
    confirmationRef: row.confirmation_ref || undefined,
    contractSigned: row.contract_signed,
    contractDate: row.contract_date || undefined,
    contractLink: row.contract_link || undefined,
    specialTerms: row.special_terms || undefined,
    // Postgres `numeric` arrives as a STRING over the wire, and the row type
    // is asserted rather than validated, so nothing catches it. The symptom is
    // silent: `"2400000".toLocaleString()` returns the string untouched, so
    // VB-011's sidebar rendered ₦2400000 beside a correctly formatted
    // ₦1,200,000 deposit — the deposit is derived via Math.round, so it was a
    // real number. Coerce at the boundary, where the shape is known.
    totalAmount: Number(row.total_amount) || 0,
    depositPercent: Number(row.deposit_percent) || 0,
    depositDueDate: row.deposit_due_date || undefined,
    depositPaid: row.deposit_paid,
    depositPaidDate: row.deposit_paid_date || undefined,
    balanceDueDate: row.balance_due_date || undefined,
    balancePaid: row.balance_paid,
    balancePaidDate: row.balance_paid_date || undefined,
    lastContactDate: row.last_contact_date || undefined,
    nextFollowupDate: row.next_followup_date || undefined,
    contactMethod: (row.contact_method as VendorBooking['contactMethod']) || undefined,
    contactNotes: row.contact_notes || undefined,
    // Not yet in the shared VendorBookingSchema — same passthrough
    // reasoning as movement_type in LogisticsService.
    contingencyAmount: Number((row as unknown as Record<string, unknown>).contingency_amount) || 0,
    // Also not in the shared schema yet — the route carries it across by hand.
    currency: (String((row as unknown as Record<string, unknown>).currency || '') || undefined),
    updatedAt: row.updated_at,
  };
}

// ── In-memory cache (no localStorage for app data) ──
let bookingsCache: Record<string, VendorBooking> = {};

export function loadBookings(): Record<string, VendorBooking> {
  return bookingsCache;
}

// Local cache only. The /vendor-bookings API is disabled (decommissioned
// 2026-09-30, bookings moved to Kalligo), so there is no write-through.
export function saveBooking(booking: VendorBooking): void {
  const nextBooking = { ...booking, updatedAt: new Date().toISOString() };
  bookingsCache = { ...bookingsCache, [booking.vendorId]: nextBooking };
  window.dispatchEvent(new CustomEvent(VENDOR_BOOKINGS_UPDATED_EVENT, { detail: nextBooking }));
}

// A booking-shaped equality check, not a reference one. usePlanningSync polls
// this every 30s (REFRESH_INTERVAL_MS in usePlanningSync.ts) for as long as
// the app is open, and every tick built a brand-new object here — even when
// the server returned byte-identical data. VendorTable's "Latest Status"
// column renders two structurally different subtrees depending on whether
// `bookings[row.apiKey]` resolves (the coloured booking-status chip vs the
// plain Verified/Pending fallback), so any poll whose freshly-built map
// leaves a vendor's key out (a transient/partial response, a request that
// raced an in-flight write) swapped that row to the fallback for one tick
// and back on the next — the "Latest Status" badge visibly flashing. Only
// touch the cache, and only notify listeners, when the content actually
// changed.
function bookingsEqual(a: Record<string, VendorBooking>, b: Record<string, VendorBooking>): boolean {
  const aKeys = Object.keys(a);
  const bKeys = Object.keys(b);
  if (aKeys.length !== bKeys.length) return false;
  return aKeys.every((key) => JSON.stringify(a[key]) === JSON.stringify(b[key]));
}

// ── API-first refresh ──

export async function refreshBookingsFromApi(): Promise<Record<string, VendorBooking>> {
  try {
    const response = await apiGet<{ bookings: ApiVendorBooking[] }>(planningPath('/vendor-bookings'));
    const bookings = (response.bookings || []).reduce<Record<string, VendorBooking>>((acc, row) => {
      const booking = transformApiBooking(row);
      acc[booking.vendorId] = booking;
      return acc;
    }, {});

    if (Object.keys(bookings).length > 0 && !bookingsEqual(bookings, bookingsCache)) {
      bookingsCache = bookings;
      window.dispatchEvent(new CustomEvent(VENDOR_BOOKINGS_UPDATED_EVENT));
    }
    return Object.keys(bookings).length > 0 ? bookings : loadBookings();
  } catch {
    return loadBookings();
  }
}
